import {api} from '../../api.js';
import {AuthSnapshot} from '../../auth.js';
import {apiUrl} from '../../config.js';
import {defaultEnterpriseSpaceLayout, enterpriseSpaceBlockText, enterpriseSpaceBlockVisible, enterpriseSpaceNoteVisibleTo, normalizeEnterpriseSpaceLayout, type EnterpriseSpaceLayout, type EnterpriseSpaceViewer} from '../../../common/clientPageLayout.js';
import {escapeHtml,renderLayout} from '../../layout.js';
import {enterpriseSpaceNoteMarkup} from './enterprisePageNotes.js';

const shortUid=(value:string):string=>((value||'').replace(/^usr_/i,'').replace(/[^0-9a-z]/gi,'').toUpperCase()+'00000000').slice(0,8);
const time=(value:string):string=>value?new Date(value).toLocaleDateString('zh-CN'):'—';
let memberMenuDocumentCloser:((event:MouseEvent)=>void)|null=null;
let memberMenuScrollCloser:((event:Event)=>void)|null=null;

/**
 * 成员头像：优先内联 data URL；否则通过头像接口懒加载（避免成员 JSON/HTML 体积过大导致不显示）。
 */
function memberAvatarMarkup(member:{id:string;displayName?:string;email?:string;avatarDataUrl?:string},auth:AuthSnapshot):string{
  const initial=escapeHtml((member.displayName||member.email||'用').slice(0,1));
  const inline=(member.avatarDataUrl||'').trim()||(member.id===auth.user.id?(auth.user.avatarDataUrl||'').trim():'');
  if(inline){
    return `<span class="enterprise-request-avatar has-image"><img src="${escapeHtml(inline)}" alt=""></span>`;
  }
  const src=escapeHtml(apiUrl(`/api/auth/users/${encodeURIComponent(member.id)}/avatar`));
  return `<span class="enterprise-request-avatar enterprise-request-avatar--fetch"><span class="enterprise-request-avatar-fallback">${initial}</span><img src="${src}" alt="" loading="lazy" decoding="async"></span>`;
}

/** 头像 URL 加载成功后再切换为图片，失败则保留首字。 */
function wireMemberAvatarFetches(section:HTMLElement):void{
  section.querySelectorAll<HTMLElement>('.enterprise-request-avatar--fetch').forEach(host=>{
    const img=host.querySelector('img');
    if(!img)return;
    img.addEventListener('load',()=>{
      if(img.naturalWidth>0)host.classList.add('has-image');
    });
    img.addEventListener('error',()=>{img.remove();});
  });
}

/**
 * 后台代开通的企业订阅，在服务名称后标明来源。
 */
function adminGrantMark(subscription:{grantType?:string}):string{
  return subscription.grantType==='admin_granted'?'<span class="enterprise-seat-admin-grant">由后台管理系统开通</span>':'';
}

/**
 * 同一服务的各批已购席位，按到期日分行显示。
 * 没有截止日期的批次显示为长期有效。
 */
function seatBatchMarkup(batches:Array<{seatQuantity?:number;expiresAt?:string|null}>,formatDate:(value:string)=>string):string{
  const ordered=[...batches].sort((left,right)=>{
    const leftTime=left.expiresAt?Date.parse(left.expiresAt):Number.POSITIVE_INFINITY;
    const rightTime=right.expiresAt?Date.parse(right.expiresAt):Number.POSITIVE_INFINITY;
    return leftTime-rightTime;
  });
  return `<span class="enterprise-seat-batches">${ordered.map((batch)=>{
    const quantity=Math.max(0,Number(batch.seatQuantity)||0);
    const expiry=batch.expiresAt?`有效期至 ${formatDate(batch.expiresAt)}`:'长期有效';
    return `<span class="enterprise-seat-batch">${quantity}个席位 ${escapeHtml(expiry)}</span>`;
  }).join('')}</span>`;
}

/**
 * 生成可分配席位的成员选项（含最高管理员本人；已持有该模块席位的成员不再出现）。
 */
function seatAssigneeOptions(members:any[],productId:string,activeSeats:(userId:string)=>any[],currentUserId:string):string{
  return members
    .filter(member=>!activeSeats(member.id).some((seat:any)=>seat.productId===productId))
    .map(member=>{
      const label=`${member.displayName||member.email}${member.id===currentUserId?'（本人）':''}`;
      return `<option value="${escapeHtml(member.id)}">${escapeHtml(label)}</option>`;
    })
    .join('');
}

export async function renderEnterpriseManagementSection(host:HTMLElement,auth:AuthSnapshot,reload:()=>void,layout?:EnterpriseSpaceLayout,team?:{id:string;ownerUserId:string;role?:string}):Promise<void>{
  const sessionTeam=auth.organization.organizationType==='team'?auth.organization:null;
  const organizationId=team?.id||sessionTeam?.id||'';
  if(!organizationId)return;
  const ownerUserId=team?.ownerUserId||sessionTeam?.ownerUserId||'';
  const teamRole=team?.role||(sessionTeam?auth.role:'');
  const isOwner=ownerUserId===auth.user.id;
  const canManage=isOwner||teamRole==='enterprise_admin';
  const pageLayout=layout??await api.get<{layout:EnterpriseSpaceLayout}>(apiUrl('/api/client-pages/enterprise-space')).then((data)=>normalizeEnterpriseSpaceLayout(data.layout)).catch(()=>defaultEnterpriseSpaceLayout());
  const viewer:EnterpriseSpaceViewer=isOwner?'owner':teamRole==='enterprise_admin'?'admin':'member';
  const showMembers=enterpriseSpaceBlockVisible(pageLayout,'members',viewer);
  const showSeats=canManage&&enterpriseSpaceBlockVisible(pageLayout,'seats',viewer);
  const managementNotes=enterpriseSpaceNoteMarkup(pageLayout.notes.filter((note)=>(note.audience==='managers'||note.audience==='owner')&&enterpriseSpaceNoteVisibleTo(note,viewer)));
  const membersTitle=escapeHtml(enterpriseSpaceBlockText(pageLayout,'members','title')||'团队成员与授权');
  const seatsTitle=escapeHtml(enterpriseSpaceBlockText(pageLayout,'seats','title')||'服务功能席位分配');
  if(!showMembers&&!showSeats&&!managementNotes)return;
  const section=document.createElement('div');
  section.className='enterprise-management-section';
  if(!showMembers&&!showSeats){section.innerHTML=managementNotes;host.append(section);return;}
  section.innerHTML='<section class="organization-loading">正在加载团队数据…</section>';
  host.append(section);

  try{
    const orgPath=encodeURIComponent(organizationId);
    const [memberData,requestData,seatData,productData,subscriptionData]=await Promise.all([
      showMembers?api.get<any>(apiUrl(`/api/organizations/${orgPath}/members`)):Promise.resolve({members:[]}),
      canManage&&showMembers?api.get<any>(apiUrl(`/api/organizations/${orgPath}/membership-request-details`)):Promise.resolve({requests:[]}),
      showMembers||showSeats?api.get<any>(apiUrl(`/api/organizations/${orgPath}/module-seats`)).catch(()=>({seats:[]})):Promise.resolve({seats:[]}),
      api.get<any>(apiUrl('/api/billing/products')).catch(()=>({products:[]})),
      showSeats?api.get<any>(apiUrl('/api/billing/subscriptions')):Promise.resolve({subscriptions:[]})
    ]);

    const members=memberData.members??[];
    const allRequests=requestData.requests??[];
    const pendingRequests=allRequests.filter((item:any)=>item.status==='pending');
    const seats=seatData.seats??[];
    const products=new Map<string,any>((productData.products??[]).map((item:any):[string,any]=>[String(item.id),item]));
    const subscriptions=(subscriptionData.subscriptions??[]).filter((item:any)=>item.ownerType==='enterprise'&&item.status==='active');
    const activeSeats=(userId:string)=>seats.filter((seat:any)=>seat.userId===userId&&seat.status==='active');
    const memberRole=(member:any)=>member.id===ownerUserId?'最高管理员':member.role==='enterprise_admin'?'管理员':'成员';
    const canBulkSelectMember=(member:any)=>member.id!==auth.user.id&&member.id!==ownerUserId;

    const authorizations=[...new Set(members.map((member:any)=>{
      const assigned=activeSeats(member.id);
      return assigned.map((seat:any)=>products.get(seat.productId)?.name||'服务功能').join('、')||'未授权';
    }).filter(value=>value&&value!=='未授权'))];

    section.innerHTML=`
      ${managementNotes}
      <section class="organization-card enterprise-main-surface">
        <div data-enterprise-block="members"${showMembers?'':' hidden'}>
        <div class="enterprise-surface-header">
          <div class="enterprise-surface-title">
            <h2>${membersTitle}</h2>
            <span class="enterprise-member-count">共 ${members.length} 人</span>
          </div>
          ${canManage?`<div class="enterprise-surface-actions">
            <button class="secondary-button enterprise-request-history" type="button">申请历史记录${pendingRequests.length?` (${pendingRequests.length}条待处理)`:''}</button>
            <button class="primary-button" id="openAddMemberBtn" type="button">+ 添加成员</button>
          </div>`:''}
        </div>

        <div class="enterprise-member-filters">
          <div class="enterprise-member-keyword-group">
            <input data-member-filter="keyword" type="search" placeholder="搜索成员名称、手机号或邮箱号" autocomplete="off">
            <button class="primary-button" type="button" data-member-filter-confirm>搜索</button>
          </div>
          <select data-member-filter="authorization">
            <option value="">全部服务授权</option>
            <option value="未授权">未授权</option>
            ${authorizations.map(value=>`<option value="${escapeHtml(value)}">${escapeHtml(value)}</option>`).join('')}
          </select>
          ${canManage?`<div class="enterprise-member-batch-bar" data-member-batch-bar hidden>
            <span data-member-batch-count>已选 0 人</span>
            <button class="danger-button" type="button" data-member-batch-remove disabled>批量移除</button>
          </div>`:''}
        </div>

        <div class="table-scroll">
          <table class="organization-members-table enterprise-members-table">
            <thead>
              <tr>
                ${canManage?'<th class="enterprise-member-select-col"><input type="checkbox" data-member-select-all aria-label="全选可移除成员" title="全选"></th>':''}
                <th>成员</th>
                <th>UID</th>
                <th>团队角色</th>
                <th>加入时间</th>
                <th>服务授权</th>
                ${canManage?'<th>操作</th>':''}
              </tr>
            </thead>
            <tbody>
              ${members.map((member:any)=>{
                const assigned=activeSeats(member.id);
                const grantNames=assigned.map((seat:any)=>products.get(seat.productId)?.name||'服务功能');
                const seatExpiry=(seat:any)=>{
                  if(!seat.endsAt)return '长期有效';
                  const date=new Date(seat.endsAt);
                  if(Number.isNaN(date.getTime()))return '长期有效';
                  return `${date.getFullYear()}年${date.getMonth()+1}月${date.getDate()}日 到期`;
                };
                const roleActions=canManage&&isOwner&&member.id!==auth.user.id?`<button type="button" role="menuitem" data-admin-user="${escapeHtml(member.id)}" data-admin-enabled="${member.role==='enterprise_admin'?'false':'true'}">${member.role==='enterprise_admin'?'撤销管理员':'设为管理员'}</button><button type="button" role="menuitem" data-transfer-owner="${escapeHtml(member.id)}">移交最高管理权</button>`:'';
                const remove=canManage&&member.id!==auth.user.id?`<button type="button" role="menuitem" class="is-danger" data-remove-member="${escapeHtml(member.id)}">移除</button>`:'';
                const memberMenu=canManage?(roleActions||remove?`<div class="enterprise-member-menu"><button type="button" class="enterprise-member-menu-trigger" data-member-menu aria-haspopup="menu" aria-expanded="false" aria-label="成员操作"><svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="5" cy="12" r="1.7" fill="currentColor"/><circle cx="12" cy="12" r="1.7" fill="currentColor"/><circle cx="19" cy="12" r="1.7" fill="currentColor"/></svg></button><div class="enterprise-member-menu-panel" role="menu" hidden>${roleActions}${remove}</div></div>`:'—'):'';
                const bulkSelect=canManage&&canBulkSelectMember(member)?`<input type="checkbox" data-member-select="${escapeHtml(member.id)}" aria-label="选择成员">`:'';
                const grantMarkup=assigned.length?`<div class="enterprise-seat-list">${assigned.map((seat:any)=>`<span class="enterprise-seat-tag"><span class="enterprise-seat-name">${escapeHtml(products.get(seat.productId)?.name||'服务功能')}${canManage?` <button class="enterprise-seat-revoke" data-revoke-seat="${escapeHtml(seat.id)}" title="撤销授权">×</button>`:''}</span><small class="enterprise-seat-expiry">${escapeHtml(seatExpiry(seat))}</small></span>`).join('')}</div>`:'<span class="enterprise-seat-empty">未授权</span>';
                return `<tr data-member-id="${escapeHtml(member.id)}" data-member-name="${escapeHtml(String(member.displayName||'').toLowerCase())}" data-member-phone="${escapeHtml(String(member.phone||''))}" data-member-email="${escapeHtml(String(member.email||'').toLowerCase())}" data-member-grants="${escapeHtml(grantNames.join('、')||'未授权')}" data-member-removable="${canManage&&canBulkSelectMember(member)?'true':'false'}">
                  ${canManage?`<td class="enterprise-member-select-col">${bulkSelect}</td>`:''}
                  <td>
                    <div class="enterprise-member-cell">
                      ${memberAvatarMarkup(member,auth)}
                      <div class="enterprise-member-info">
                        <strong>${escapeHtml(member.displayName||'未设置名称')}</strong>
                        <small>${escapeHtml(member.phone||member.email||'—')}</small>
                      </div>
                    </div>
                  </td>
                  <td><code>${escapeHtml(shortUid(member.id))}</code></td>
                  <td><span class="enterprise-role-tag is-${member.role||'member'}">${memberRole(member)}</span></td>
                  <td>${escapeHtml(time(member.joinedAt||member.createdAt))}</td>
                  <td>${grantMarkup}</td>
                  ${canManage?`<td class="enterprise-table-actions">${memberMenu}</td>`:''}
                </tr>`;
              }).join('')}
            </tbody>
          </table>
        </div>
        </div>

        ${showSeats?`<div class="enterprise-surface-divider"${showMembers?'':' hidden'}></div>
        <div data-enterprise-block="seats">
        <div class="enterprise-surface-header">
          <div class="enterprise-surface-title">
            <h2>${seatsTitle}</h2>
          </div>
        </div>

        <div class="table-scroll">
          <table class="organization-members-table enterprise-seats-table">
            <thead>
              <tr>
                <th>服务功能模块</th>
                <th>已分配 / 已购席位</th>
                <th>快捷授权给成员</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              ${[...subscriptions.reduce((groups:Map<string,any[]>,subscription:any)=>{
                const productId=String(subscription.productId||'');
                const batches=groups.get(productId)??[];
                batches.push(subscription);
                groups.set(productId,batches);
                return groups;
              },new Map<string,any[]>()).values()].map((batches:any[])=>{
                const productId=String(batches[0]?.productId||'');
                const used=seats.filter((seat:any)=>seat.productId===productId&&seat.status==='active').length;
                const adminBatch=batches.find((batch:any)=>batch.grantType==='admin_granted');
                return `<tr>
                  <td><strong>${escapeHtml(products.get(productId)?.name||'服务功能')}</strong>${adminBatch?adminGrantMark(adminBatch):''}</td>
                  <td><span class="enterprise-seat-usage">已分配 ${used} 席</span>${seatBatchMarkup(batches,time)}</td>
                  <td>
                    <select data-seat-user="${escapeHtml(productId)}">
                      <option value="">选择要授权的成员...</option>
                      ${seatAssigneeOptions(members,productId,activeSeats,auth.user.id)}
                    </select>
                  </td>
                  <td><button class="primary-button enterprise-assign-btn" data-assign-product="${escapeHtml(productId)}">分配席位</button></td>
                </tr>`;
              }).join('')||'<tr><td colspan="4" class="organization-empty">暂无可分配的企业服务功能</td></tr>'}
            </tbody>
          </table>
        </div>
        </div>`:''}
      </section>
    `;

    wireMemberAvatarFetches(section);

    const refresh=()=>reload();

    // 搜索与筛选逻辑
    const memberTable=section.querySelector<HTMLTableElement>('.enterprise-members-table');
    const batchBar=section.querySelector<HTMLElement>('[data-member-batch-bar]');
    const batchCount=section.querySelector<HTMLElement>('[data-member-batch-count]');
    const batchRemoveBtn=section.querySelector<HTMLButtonElement>('[data-member-batch-remove]');
    const selectAll=section.querySelector<HTMLInputElement>('[data-member-select-all]');

    const selectedMemberIds=():string[]=>{
      if(!memberTable)return[];
      return [...memberTable.querySelectorAll<HTMLInputElement>('[data-member-select]:checked')].map(input=>input.dataset.memberSelect||'').filter(Boolean);
    };

    const syncMemberBatchBar=():void=>{
      const ids=selectedMemberIds();
      if(batchBar)batchBar.hidden=ids.length===0;
      if(batchCount)batchCount.textContent=`已选 ${ids.length} 人`;
      if(batchRemoveBtn)batchRemoveBtn.disabled=ids.length===0;
      if(selectAll&&memberTable){
        const selectable=[...memberTable.querySelectorAll<HTMLTableRowElement>('tbody tr[data-member-removable="true"]')].filter(row=>!row.hidden);
        const checked=selectable.filter(row=>row.querySelector<HTMLInputElement>('[data-member-select]')?.checked);
        selectAll.indeterminate=checked.length>0&&checked.length<selectable.length;
        selectAll.checked=selectable.length>0&&checked.length===selectable.length;
      }
    };

    if(memberTable){
      const keyword=section.querySelector<HTMLInputElement>('[data-member-filter="keyword"]');
      const authorization=section.querySelector<HTMLSelectElement>('[data-member-filter="authorization"]');
      const filterRows=()=>{
        const text=(keyword?.value||'').toLowerCase();
        const service=authorization?.value||'';
        [...memberTable.tBodies[0].rows].forEach(row=>{
          const searchable=`${row.dataset.memberName||''} ${row.dataset.memberPhone||''} ${row.dataset.memberEmail||''}`;
          const granted=row.dataset.memberGrants||'';
          row.hidden=Boolean((text&&!searchable.includes(text))||(service&&!granted.includes(service)));
        });
        syncMemberBatchBar();
      };
      section.querySelector<HTMLButtonElement>('[data-member-filter-confirm]')?.addEventListener('click',filterRows);
      keyword?.addEventListener('keydown',event=>{
        if(event.key==='Enter'){
          event.preventDefault();
          filterRows();
        }
      });
      authorization?.addEventListener('change',filterRows);

      memberTable.addEventListener('change',event=>{
        const target=event.target as HTMLElement;
        if(target.matches('[data-member-select],[data-member-select-all]')){
          if(target.matches('[data-member-select-all]')){
            const checked=(target as HTMLInputElement).checked;
            [...memberTable.querySelectorAll<HTMLTableRowElement>('tbody tr[data-member-removable="true"]')].forEach(row=>{
              if(row.hidden)return;
              const box=row.querySelector<HTMLInputElement>('[data-member-select]');
              if(box)box.checked=checked;
            });
          }
          syncMemberBatchBar();
        }
      });
    }

    batchRemoveBtn?.addEventListener('click',async()=>{
      const ids=selectedMemberIds();
      if(!ids.length)return;
      const label=ids.length===1?'确定从本团队移除该成员吗？其服务席位将被撤销。':`确定从本团队移除已选的 ${ids.length} 名成员吗？其服务席位将被撤销。`;
      if(!window.confirm(label))return;
      batchRemoveBtn.disabled=true;
      const failures:string[]=[];
      for(const userId of ids){
        try{
          await api.delete(apiUrl(`/api/organizations/${organizationId}/members/${encodeURIComponent(userId)}`));
        }catch(error){
          failures.push(error instanceof Error?error.message:String(error));
        }
      }
      if(failures.length){
        window.alert(failures.length===ids.length?failures[0]:`部分成员移除失败（${failures.length}/${ids.length}）：${failures[0]}`);
        batchRemoveBtn.disabled=false;
        syncMemberBatchBar();
        if(failures.length<ids.length)refresh();
        return;
      }
      refresh();
    });

    // “+ 添加成员”独立弹出窗口
    section.querySelector('#openAddMemberBtn')?.addEventListener('click',()=>{
      const modal=document.createElement('div');
      modal.className='modal-overlay show';
      modal.innerHTML=`
        <div class="modal enterprise-add-member-modal">
          <div class="modal-header">
            <div>
              <h3>添加团队成员</h3>
              <p class="modal-subtitle">输入被邀请人的邮箱地址或 11 位手机号码，发送加入邀请</p>
            </div>
            <button class="modal-close" type="button">×</button>
          </div>
          <div class="modal-body">
            <form id="enterpriseInviteForm" class="organization-form">
              <label>
                <span>用户邮箱或手机号</span>
                <input name="account" type="text" placeholder="name@example.com 或 13800138000" required autocomplete="off">
              </label>
              <div class="modal-form-status">
                <span id="enterpriseInviteMessage" role="status"></span>
              </div>
              <div class="modal-footer-actions">
                <button class="secondary-button modal-cancel-btn" type="button">取消</button>
                <button class="primary-button" type="submit">发送邀请</button>
              </div>
            </form>
          </div>
        </div>
      `;
      document.body.append(modal);

      const form=modal.querySelector<HTMLFormElement>('#enterpriseInviteForm');
      const message=modal.querySelector<HTMLElement>('#enterpriseInviteMessage');
      const close=()=>{modal.remove();};

      modal.addEventListener('click',event=>{
        if(event.target===modal||(event.target as HTMLElement).closest('.modal-close,.modal-cancel-btn')) close();
      });

      form?.addEventListener('submit',async event=>{
        event.preventDefault();
        try{
          const submitBtn=form.querySelector<HTMLButtonElement>('button[type="submit"]');
          if(submitBtn){submitBtn.disabled=true;submitBtn.textContent='发送中...';}
          await api.post(apiUrl(`/api/organizations/${organizationId}/membership-invitations`),{
            email:String(new FormData(form).get('account')||'').trim()
          });
          if(message)message.textContent='已成功发送邀请';
          window.setTimeout(()=>{close();refresh();},600);
        }catch(error){
          if(message)message.textContent=error instanceof Error?error.message:String(error);
          const submitBtn=form.querySelector<HTMLButtonElement>('button[type="submit"]');
          if(submitBtn){submitBtn.disabled=false;submitBtn.textContent='发送邀请';}
        }
      });
      (form?.querySelector('input[name="account"]') as HTMLInputElement)?.focus();
    });

    // 申请历史记录 Modal
    section.querySelector<HTMLButtonElement>('.enterprise-request-history')?.addEventListener('click',()=>{
      const modal=document.createElement('div');
      modal.className='modal-overlay show';
      modal.innerHTML=`
        <div class="modal enterprise-request-modal">
          <div class="modal-header">
            <div>
              <h3>申请历史记录</h3>
              <p class="modal-subtitle">团队成员申请与邀请处理记录</p>
            </div>
            <button class="modal-close" type="button">×</button>
          </div>
          <div class="modal-body">
            <div class="table-scroll">
              <table class="organization-members-table enterprise-request-table">
                <thead>
                  <tr>
                    <th>UID</th>
                    <th>头像</th>
                    <th>用户名称</th>
                    <th>邮箱号</th>
                    <th>手机号</th>
                    <th>申请/邀请时间</th>
                    <th>状态 / 操作</th>
                  </tr>
                </thead>
                <tbody>
                  ${allRequests.map((item:any)=>{
                    const user=item.user||{};
                    const decision=item.status==='approved'?'<span class="enterprise-request-decision is-approved">已同意</span>':
                      item.status==='rejected'?'<span class="enterprise-request-decision is-rejected">已拒绝</span>':
                      item.direction==='apply'?`<button class="secondary-button" data-modal-request="${escapeHtml(item.id)}" data-approved="true">同意</button> <button class="danger-button" data-modal-request="${escapeHtml(item.id)}" data-approved="false">拒绝</button>`:'<span class="enterprise-request-pending">待对方接受</span>';
                    return `<tr>
                      <td><code>${escapeHtml(shortUid(user.id||item.userId||''))}</code></td>
                      <td>${memberAvatarMarkup({...user,id:user.id},auth)}</td>
                      <td><strong>${escapeHtml(user.displayName||'未设置名称')}</strong></td>
                      <td>${escapeHtml(user.email||'—')}</td>
                      <td>${escapeHtml(user.phone||'—')}</td>
                      <td>${escapeHtml(time(item.createdAt))}</td>
                      <td>${decision}</td>
                    </tr>`;
                  }).join('')||'<tr><td colspan="7" class="organization-empty">暂无申请记录</td></tr>'}
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button class="primary-button modal-close-btn" type="button">关闭</button>
          </div>
        </div>
      `;
      document.body.append(modal);

      modal.addEventListener('click',async event=>{
        const target=event.target as HTMLElement;
        if(target===modal||target.closest('.modal-close,.modal-close-btn')) { modal.remove(); return; }
        const btn=target.closest<HTMLButtonElement>('[data-modal-request]');
        if(btn){
          try{
            await api.post(apiUrl(`/api/organizations/membership-requests/${btn.dataset.modalRequest}/respond`),{approved:btn.dataset.approved==='true'});
            modal.remove();
            refresh();
          }catch(error){ window.alert(error instanceof Error?error.message:String(error)); }
        }
      });
    });

    // 表格操作代理监听（管理员变更、移除成员、分配席位、撤销席位）
    const closeMemberMenus=()=>{
      section.querySelectorAll<HTMLElement>('.enterprise-member-menu-panel').forEach(panel=>{panel.hidden=true;});
      section.querySelectorAll<HTMLButtonElement>('[data-member-menu]').forEach(trigger=>trigger.setAttribute('aria-expanded','false'));
    };
    /**
     * 把成员操作菜单固定到三点按钮旁边，避免被表格滚动区域裁切。
     */
    const openMemberMenu=(trigger:HTMLElement,panel:HTMLElement)=>{
      panel.hidden=false;
      trigger.setAttribute('aria-expanded','true');
      const rect=trigger.getBoundingClientRect();
      const width=Math.max(panel.offsetWidth,168);
      const height=panel.offsetHeight;
      const left=Math.max(8,Math.min(rect.right-width,window.innerWidth-width-8));
      const below=rect.bottom+6;
      const top=below+height>window.innerHeight-8?Math.max(8,rect.top-height-6):below;
      panel.style.left=`${left}px`;
      panel.style.top=`${top}px`;
    };
    const onDocumentClick=(event:MouseEvent)=>{
      if(!section.isConnected){document.removeEventListener('click',onDocumentClick);return;}
      if((event.target as HTMLElement).closest('.enterprise-member-menu'))return;
      closeMemberMenus();
    };
    const onMenuScroll=()=>{
      if(!section.isConnected){document.removeEventListener('scroll',onMenuScroll,true);return;}
      closeMemberMenus();
    };
    if(memberMenuDocumentCloser)document.removeEventListener('click',memberMenuDocumentCloser);
    if(memberMenuScrollCloser)document.removeEventListener('scroll',memberMenuScrollCloser,true);
    memberMenuDocumentCloser=onDocumentClick;
    memberMenuScrollCloser=onMenuScroll;
    document.addEventListener('click',onDocumentClick);
    document.addEventListener('scroll',onMenuScroll,true);
    section.addEventListener('click',async event=>{
      if(!canManage)return;
      const target=event.target as HTMLElement;
      const trigger=target.closest<HTMLButtonElement>('[data-member-menu]');
      if(trigger){
        const panel=trigger.parentElement?.querySelector<HTMLElement>('.enterprise-member-menu-panel');
        const opening=Boolean(panel?.hidden);
        closeMemberMenus();
        if(opening&&panel)openMemberMenu(trigger,panel);
        return;
      }
      const button=target.closest<HTMLButtonElement>('[data-admin-user],[data-transfer-owner],[data-remove-member],[data-assign-product],[data-revoke-seat]');
      if(!button)return;
      if(button.dataset.adminUser||button.dataset.transferOwner||button.dataset.removeMember)closeMemberMenus();
      try{
        if(button.dataset.adminUser){
          await api.patch(apiUrl(`/api/organizations/${organizationId}/members/${button.dataset.adminUser}/administrator`),{enabled:button.dataset.adminEnabled==='true'});
          refresh();
        }else if(button.dataset.transferOwner){
          if(!window.confirm('确定移交最高管理员身份吗？'))return;
          await api.patch(apiUrl(`/api/organizations/${organizationId}/owner`),{userId:button.dataset.transferOwner});
          refresh();
        }else if(button.dataset.removeMember){
          if(!window.confirm('确定从本团队移除该用户吗？'))return;
          await api.delete(apiUrl(`/api/organizations/${organizationId}/members/${button.dataset.removeMember}`));
          refresh();
        }else if(button.dataset.assignProduct){
          const productId=button.dataset.assignProduct||'';
          const userId=section.querySelector<HTMLSelectElement>(`[data-seat-user="${CSS.escape(productId)}"]`)?.value||'';
          if(!userId){window.alert('请选择要授权的成员');return;}
          await api.post(apiUrl(`/api/organizations/${organizationId}/module-seats`),{productId,userId});
          refresh();
        }else if(button.dataset.revokeSeat){
          if(!window.confirm('确定撤销该服务授权吗？'))return;
          await api.delete(apiUrl(`/api/organizations/${organizationId}/module-seats/${button.dataset.revokeSeat}`));
          refresh();
        }
      }catch(error){
        window.alert(error instanceof Error?error.message:String(error));
      }
    });

  }catch(error){
    section.innerHTML=`<section class="organization-loading is-error">团队空间管理加载失败：${escapeHtml(error instanceof Error?error.message:String(error))}</section>`;
  }
}

export async function renderEnterpriseAccountPage(root:HTMLElement,auth:AuthSnapshot):Promise<void>{
  renderLayout(root,auth,'');
  await renderEnterpriseManagementSection(root.querySelector<HTMLElement>('.content')!,auth,()=>void renderEnterpriseAccountPage(root,auth));
}
